import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Apple, Carrot, Milk, Croissant, Beef, Wine } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Skeleton } from '@/components/ui/skeleton';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const iconMap = {
  Apple: Apple,
  Carrot: Carrot,
  Milk: Milk,
  Croissant: Croissant,
  Beef: Beef,
  Wine: Wine,
};

const CategorySection = () => {
  const { t, getLocalizedField } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  if (loading) {
    return (
      <section className="py-16 md:py-24" data-testid="category-section-loading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10">
            <div>
              <Skeleton className="h-8 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-10 w-24" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-3xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="category-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-between items-end mb-10 md:mb-12"
        >
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              {t('categories.title')}
            </h2>
            <p className="text-slate-500">{t('categories.subtitle')}</p>
          </div>
          <Link
            to="/category/all"
            className="hidden sm:flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
            data-testid="view-all-categories-link"
          >
            {t('categories.viewAll')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        {/* Category Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6"
        >
          {categories.map((category) => {
            const IconComponent = iconMap[category.icon] || Apple;
            return (
              <motion.div key={category.id} variants={itemVariants}>
                <Link
                  to={`/category/${category.slug}`}
                  className="group block"
                  data-testid={`category-card-${category.slug}`}
                >
                  <motion.div
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="relative aspect-square rounded-3xl overflow-hidden card-glass"
                  >
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      <img
                        src={category.image}
                        alt={getLocalizedField(category, 'name')}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    </div>

                    {/* Content */}
                    <div className="absolute inset-0 flex flex-col items-center justify-end p-4 text-white">
                      <motion.div
                        className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mb-3"
                        whileHover={{ rotate: 10 }}
                      >
                        <IconComponent className="w-6 h-6" style={{ color: category.color }} />
                      </motion.div>
                      <h3 className="font-semibold text-center">
                        {getLocalizedField(category, 'name')}
                      </h3>
                      <p className="text-xs text-white/70">
                        {category.product_count} {t('categories.products')}
                      </p>
                    </div>

                    {/* Hover overlay */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background: `linear-gradient(135deg, ${category.color}20 0%, transparent 100%)`,
                      }}
                    />
                  </motion.div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Mobile View All */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-8 text-center sm:hidden"
        >
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 text-primary font-medium"
          >
            {t('categories.viewAll')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default CategorySection;
